import React from 'react';

// 阿豆 (Ado) — the mascot. Geometry and the reasoning behind it live in
// docs/design/mascot-ado.html; this file is the only implementation of it.
//
// It is the app icon (public/icons/app/icon.svg) with eyes: the open ring is
// its head, the upright bar beside it is its tail. **Tail height is the state
// indicator** — asleep it sits flush with the top of the head, which is the
// logo unchanged; awake it rises. That is what makes the mark survive the one
// place it actually lives: a 24px slot in the header, where the eyes are
// dropped and what is left is the favicon.
//
// Colors are literal brand hex rather than the --brand-* tokens on purpose.
// Those tokens flip in dark mode (--brand-primary becomes pink), and a mascot
// that changes species with the theme is not a mascot. The cream face and ink
// eyes read on both backgrounds, so nothing needs to flip.
const CORAL = '#FF5964';
const LEMON = '#FFEC51';
const CREAM = '#FDF6F5';
const INK = '#32162B';

export type AdoState = 'sleep' | 'listen' | 'think' | 'done' | 'lost' | 'rest';

// Below this the two 15-unit eyes blur into one smudge, so they come off and
// the mark degrades back to the logo. 28 is where that happens, not a taste.
const EYES_BELOW = 28;

type Eyes = 'open' | 'shut' | 'happy' | 'squint' | 'lost';

type Pose = {
  /** Top edge of the tail. The bottom is pinned at 400, so a smaller number is a taller tail. */
  tail: number;
  eyes: Eyes;
  spark?: boolean;
  /** Whole-body lean, degrees, pivoted at the feet. */
  tilt?: number;
  /** Tail-only lean, degrees, pivoted where the tail meets the body. */
  flick?: number;
};

const POSES: Record<AdoState, Pose> = {
  sleep: { tail: 136, eyes: 'shut' },
  listen: { tail: 88, eyes: 'open' },
  think: { tail: 88, eyes: 'squint', spark: true },
  done: { tail: 88, eyes: 'happy', flick: -10 },
  lost: { tail: 168, eyes: 'lost', tilt: -8, flick: 10 },
  rest: { tail: 136, eyes: 'shut', tilt: 10 },
};

// Eye centres; everything else is drawn relative to these two points.
const LEFT = 206;
const RIGHT = 266;
const EYE_Y = 264;

function Eyes({ kind, fill }: { kind: Eyes; fill: string }) {
  if (kind === 'open') {
    return (
      <>
        <circle cx={LEFT} cy={EYE_Y} r="15" fill={fill} />
        <circle cx={RIGHT} cy={EYE_Y} r="15" fill={fill} />
      </>
    );
  }
  // One eye wide, one small: the face reads as thrown off rather than sad,
  // which is what a parse failure actually is.
  if (kind === 'lost') {
    return (
      <>
        <circle cx={LEFT} cy={EYE_Y} r="15" fill={fill} />
        <circle cx={RIGHT} cy={EYE_Y + 2} r="7" fill={fill} />
      </>
    );
  }
  const d =
    kind === 'shut'
      ? 'M192 260q14 13 28 0M252 260q14 13 28 0'
      : kind === 'happy'
        ? 'M192 270q14 -16 28 0M252 270q14 -16 28 0'
        : 'M193 264h26M253 264h26';
  return <path d={d} fill="none" stroke={fill} strokeWidth="11" strokeLinecap="round" />;
}

export default function Ado({
  state = 'listen',
  size = 24,
  tone = 'brand',
  title,
  className,
}: {
  state?: AdoState;
  /** Rendered width and height in px. Eyes come off below 28. */
  size?: number;
  /** 'invert' is for coral backgrounds — the only re-colouring the mark allows. */
  tone?: 'brand' | 'invert';
  /** Given, the mark becomes an image with this label; otherwise it is decorative. */
  title?: string;
  className?: string;
}) {
  const pose = POSES[state];
  const invert = tone === 'invert';
  const ring = invert ? CREAM : CORAL;
  const face = invert ? CORAL : CREAM;
  const tail = invert ? LEMON : CORAL;
  const eye = invert ? CREAM : INK;

  return (
    <svg
      width={size}
      height={size}
      // Cropped to the artwork. A 0 0 512 512 box left the mark filling 52% of
      // it, which at 24px meant 12.5px of actual mascot.
      viewBox="88 76 336 336"
      className={className}
      role={title ? 'img' : undefined}
      aria-hidden={title ? undefined : true}
    >
      {title && <title>{title}</title>}
      <g transform={pose.tilt ? `rotate(${pose.tilt} 256 400)` : undefined}>
        <rect
          x="316"
          y={pose.tail}
          width="52"
          height={400 - pose.tail}
          rx="26"
          fill={tail}
          transform={pose.flick ? `rotate(${pose.flick} 342 392)` : undefined}
        />
        <circle cx="236" cy="268" r="80" fill={face} />
        {/* 270° of ring; the missing quarter at the upper left is the opening
            the logo already had, kept here as a tuft of hair. */}
        <path
          d="M236 162 A106 106 0 1 1 130 268"
          fill="none"
          stroke={ring}
          strokeWidth="52"
          strokeLinecap="round"
        />
        {size >= EYES_BELOW && <Eyes kind={pose.eyes} fill={eye} />}
        {/* Capping the tail rather than floating above it: the box is cropped
            to the artwork, so there is no room above, and "the tip lit up"
            says the same thing. Same yellow as the bar's ✦ AI badge. */}
        {pose.spark && size >= EYES_BELOW && (
          <circle cx="342" cy={pose.tail + 10} r="19" fill={LEMON} />
        )}
      </g>
    </svg>
  );
}
